import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getUnixTime } from 'date-fns';
import { Formik } from 'formik';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Checkbox } from 'src/components/Checkbox';
import { Icon } from 'src/components/Icon';
import { Stack } from 'src/components/Stack';
import { StickyCard, StickyCardSkeleton } from 'src/components/StickyCard';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { PlatformRole } from 'src/models/platform/_types';
import { updatePlatform } from 'src/models/platform/platform';
import { useTrainerSettings } from 'src/pages/Staff/NewStaff/useTrainerSettings';
import {
  HQSettingsPropagationProvider,
  useOptionalHQSettingsPropagation,
} from 'src/shared/HQ/useHQSettingsPropagation';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { getChangedFields } from 'src/utils/objectUtils';
import toast from 'src/utils/toast';

import { RoleEditor } from './RoleEditor';

const RolesManagementPageContent = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'staff.rolesManagement',
  });
  const { t: tStaff } = useTranslation('translation', {
    keyPrefix: 'staff',
  });
  const { t: tGlobal } = useTranslation('translation');

  const { session, user, refreshSessionAndUser } = useSession();
  const { allReports } = useTrainerSettings();
  const queryClient = useQueryClient();

  // HQ settings propagation hook (null for non-HQ platforms)
  const hqPropagation = useOptionalHQSettingsPropagation();
  const roleRefs = useRef<{ [key: string]: HTMLElement | null }>({});
  const [expandedRoles, setExpandedRoles] = useState<{
    [key: string]: boolean;
  }>({});

  const [loading, setLoading] = useState(false);
  const [originalRoles, setOriginalRoles] = useState<string | null>(null);

  const setRoleRef = useCallback((id: string, el: HTMLElement | null) => {
    roleRefs.current[id] = el;
  }, []);

  const handleToggleRole = useCallback((roleId: Identifier) => {
    setExpandedRoles((prev) => ({
      ...prev,
      [roleId]: !prev[roleId],
    }));
  }, []);

  const updatePlatformMutation = useMutation({
    mutationFn: (data: Partial<NonNullable<typeof session>>) =>
      updatePlatform(session!.id, data, true, user),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['session'] });
    },
  });

  if (!session) {
    return <StickyCardSkeleton />;
  }

  // Store original roles for reset functionality (same as v2)
  if (!originalRoles) {
    setOriginalRoles(JSON.stringify(session.defaultChildPlatformRoles || []));
  }

  const initialValues = {
    defaultChildPlatformRoles: (session.defaultChildPlatformRoles || []).map(
      (role, index) => ({
        ...role,
        id: role.id || `existing-role-${index}`,
        permissions: {
          assessmentsRole: role.permissions?.assessmentsRole || 0,
          schedulesRole: role.permissions?.schedulesRole || 0,
          messagesRole: role.permissions?.messagesRole || 1,
          resourcesRole: role.permissions?.resourcesRole || 0,
          videosRole: role.permissions?.videosRole || 0,
          routinesRole: role.permissions?.routinesRole || 0,
          subtrainerRole: role.permissions?.subtrainerRole || 0,
          clockRole: role.permissions?.clockRole || 0,
          connectedAppsRole: role.permissions?.connectedAppsRole || 0,
          workoutPlansRole: role.permissions?.workoutPlansRole || 0,
          exercisesRole: role.permissions?.exercisesRole || 0,
          clientsRole: role.permissions?.clientsRole || 0,
          groupsRole: role.permissions?.groupsRole || 0,
          schedulingRole: role.permissions?.schedulingRole || 0,
          cancelVisitsRole: role.permissions?.cancelVisitsRole || 0,
          clientPackagesRole: role.permissions?.clientPackagesRole || 0,
          inventoryRole: role.permissions?.inventoryRole || 0,
          canViewBillingInfoRole: role.permissions?.canViewBillingInfoRole ?? 0,
          posRole: role.permissions?.posRole || 0,
          disabledTabs: role.permissions?.disabledTabs || [],
          disabledDashboardBoxes:
            role.permissions?.disabledDashboardBoxes || [],
          disabledEmails: role.permissions?.disabledEmails || [],
          disabledDashboardNotifications:
            role.permissions?.disabledDashboardNotifications || [],
          headTrainerEmails: role.permissions?.headTrainerEmails || [],
          reportsRole:
            role.permissions?.reportsRole ||
            allReports.map((report) => report.id.toString()),
        },
      })
    ),
    platform: {
      blockSitesToAddRoles: session.blockSitesToAddRoles || false,
      blockSitesToEditRoles: session.blockSitesToEditRoles || false,
    },
  };

  const handleSubmit = async (values: typeof initialValues) => {
    // Validate role names are not empty and are unique
    const roles = values.defaultChildPlatformRoles || [];
    const names = roles.map((r) => r.name.trim()).filter(Boolean);
    if (names.length !== roles.length) {
      toast.error(tStaff('roleNameRequired'));
      return;
    }
    const duplicates = names.filter(
      (name, index) => names.indexOf(name) !== index
    );
    if (duplicates.length > 0) {
      toast.error(
        tStaff('roleNameExists', {
          name: [...new Set(duplicates)].join(', '),
        })
      );
      return;
    }

    // Create the save function that will be called after HQ propagation decision
    const saveFunction = async () => {
      setLoading(true);
      try {
        // Update platform settings with block settings and default child platform roles
        await updatePlatformMutation.mutateAsync({
          defaultChildPlatformRoles: values.defaultChildPlatformRoles.map(
            (role) => ({
              id: role.id,
              name: role.name,
              permissions: role.permissions,
            })
          ),
          blockSitesToAddRoles: values.platform.blockSitesToAddRoles,
          blockSitesToEditRoles: values.platform.blockSitesToEditRoles,
        });

        await refreshSessionAndUser();

        // Clear original roles after successful save (same as v2)
        setOriginalRoles(null);

        toast.success(tGlobal('settings.platform.customizationSettingsSaved'));
      } catch (error) {
        toast.error(api.formatError(error));
      } finally {
        setLoading(false);
      }
    };

    // Let HQ hook intercept if needed, otherwise proceed with normal save
    if (hqPropagation) {
      await hqPropagation.interceptSave(
        getChangedFields(values, initialValues, true), // Enable deep comparison
        initialValues,
        saveFunction,
        t
      );
    } else {
      await saveFunction();
    }
  };

  const handleReset = (resetForm: () => void) => {
    // Reset the form to its initial values
    resetForm();

    // Clear original roles after successful reset (same as v2)
    setOriginalRoles(null);

    toast.success(t('changesDiscarded'));
  };

  const formikProps = {
    initialValues,
    enableReinitialize: true,
    validationSchema: null,
    onSubmit: handleSubmit,
  };

  return (
    <Formik {...formikProps}>
      {({ values, setFieldValue, submitForm, resetForm }) => {
        return (
          <Stack spacing={3}>
            <StickyCard
              title={
                <Stack spacing={1}>
                  <Typography variant="h4">{t('title')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('description')}
                  </Typography>
                </Stack>
              }
              actions={
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="bordered"
                    onClick={() => handleReset(resetForm)}
                  >
                    {t('reset')}
                  </Button>
                  <Button
                    variant="prominent"
                    loading={loading}
                    onClick={() => submitForm()}
                  >
                    {t('save')}
                  </Button>
                </Stack>
              }
            >
              <Stack spacing={3}>
                {/* HQ Settings Section - only show for HQ platforms */}
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('hqSettings')}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t('hqDescription')}
                  </Typography>
                  <Stack spacing={2}>
                    <Box display="flex" alignItems="center">
                      <Checkbox
                        checked={!values.platform.blockSitesToAddRoles}
                        onChange={(e) =>
                          setFieldValue(
                            'platform.blockSitesToAddRoles',
                            !e.target.checked
                          )
                        }
                      />
                      <Typography variant="body2" ml={1}>
                        {t('allowSitesAddRoles')}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <Checkbox
                        checked={!values.platform.blockSitesToEditRoles}
                        onChange={(e) =>
                          setFieldValue(
                            'platform.blockSitesToEditRoles',
                            !e.target.checked
                          )
                        }
                      />
                      <Typography variant="body2" ml={1}>
                        {t('allowSitesEditRoles')}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                {/* No Roles Message */}
                {(!values.defaultChildPlatformRoles ||
                  values.defaultChildPlatformRoles.length === 0) && (
                  <Typography variant="body2" color="text.secondary">
                    {t('noRolesMessage')}
                  </Typography>
                )}

                {/* Role Editors */}
                <Stack
                  spacing={2}
                  sx={{
                    bgcolor: 'background.level2',
                    px: 2,
                    mx: -2,
                    mb: -2,
                    py: 2,
                  }}
                >
                  {values.defaultChildPlatformRoles?.map((role, index) => (
                    <RoleEditor
                      key={role.id}
                      index={index}
                      role={role}
                      setRoleRef={setRoleRef}
                      expanded={
                        expandedRoles[role.id!] ??
                        values.defaultChildPlatformRoles.length === 1
                      }
                      onToggle={handleToggleRole}
                      onDelete={async () => {
                        const newRoles =
                          values.defaultChildPlatformRoles?.filter(
                            (_, i) => i !== index
                          );
                        setFieldValue('defaultChildPlatformRoles', newRoles);
                      }}
                      canEdit={!values.platform.blockSitesToEditRoles}
                    />
                  ))}
                </Stack>

                {/* Create New Role Button */}
                <Button
                  variant="bordered"
                  startIcon={<Icon icon={faPlus} />}
                  onClick={() => {
                    const newRoleId = `${getUnixTime(new Date())}-${
                      (values?.defaultChildPlatformRoles?.length || 0) + 1
                    }`;
                    const newRole: PlatformRole = {
                      id: newRoleId,
                      name: 'New Role',
                      permissions: {
                        assessmentsRole: 0,
                        schedulesRole: 0,
                        messagesRole: 1,
                        resourcesRole: 0,
                        videosRole: 0,
                        routinesRole: 0,
                        subtrainerRole: 0,
                        clockRole: 0,
                        connectedAppsRole: 0,
                        workoutPlansRole: 0,
                        exercisesRole: 0,
                        clientsRole: 0,
                        groupsRole: 0,
                        schedulingRole: 0,
                        cancelVisitsRole: 0,
                        clientPackagesRole: 0,
                        inventoryRole: 0,
                        canViewBillingInfoRole: 0,
                        posRole: 0,
                        disabledTabs: [],
                        disabledDashboardBoxes: [],
                        disabledEmails: [],
                        disabledDashboardNotifications: [],
                        headTrainerEmails: [],
                        reportsRole: allReports.map((report) =>
                          report.id.toString()
                        ),
                      },
                    };
                    setFieldValue('defaultChildPlatformRoles', [
                      ...(values.defaultChildPlatformRoles || []),
                      newRole,
                    ]);
                    // Expand the newly created role
                    setExpandedRoles((prev) => ({
                      ...prev,
                      [newRoleId]: true,
                    }));
                    // Scroll to the new role after a brief delay to ensure it's rendered
                    setTimeout(() => {
                      const element = roleRefs.current[newRoleId];
                      if (element) {
                        const headerOffset = 80; // Adjust this value based on your header height
                        const elementPosition = element.offsetTop;
                        const offsetPosition = elementPosition - headerOffset;

                        window.scrollTo({
                          top: offsetPosition,
                          behavior: 'smooth',
                        });
                      }
                    }, 100);
                  }}
                >
                  {t('createNewRole')}
                </Button>
              </Stack>
            </StickyCard>
          </Stack>
        );
      }}
    </Formik>
  );
};

const RolesManagementPage = () => {
  const { session } = useSession();

  // Only wrap with HQ provider for HQ platforms
  if (session?.platformType === 'hq') {
    return (
      <HQSettingsPropagationProvider>
        <RolesManagementPageContent />
      </HQSettingsPropagationProvider>
    );
  }

  return <RolesManagementPageContent />;
};

export default RolesManagementPage;
